import { applyMapping } from '../transformation-dsl.js';
const GA4_SCRIPT_ORIGIN = 'https://www.googletagmanager.com';
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/u;
/**
 * Google Analytics 4 via the gtag.js bootstrap.
 *
 * GA4 is a separate provider from GTM: it validates a `G-` measurement ID,
 * loads `gtag/js` (not `gtm.js`), and dispatches mapped events through
 * `gtag('event', …)` rather than `dataLayer` pushes. It honours the same
 * consent, mapping and failure-isolation contract as every other adapter.
 */
export class Ga4Adapter {
    provider = 'GA4';
    isLoaded = false;
    load(integration, ctx) {
        if (typeof window === 'undefined' || this.isLoaded)
            return;
        const measurementId = integration.configuration.measurementId;
        if (typeof measurementId !== 'string' || !MEASUREMENT_ID_PATTERN.test(measurementId)) {
            ctx.logger.warn(`GA4 adapter: invalid measurementId for integration ${integration.id}`);
            return;
        }
        const win = window;
        win.dataLayer = win.dataLayer ?? [];
        const dataLayer = win.dataLayer;
        // Mirrors the vendor bootstrap: gtag() forwards its call arguments onto
        // the dataLayer. The vendor pushes the `arguments` object; a plain array
        // is equivalent for gtag.js and keeps the codebase free of `arguments`.
        win.gtag = (...args) => {
            dataLayer.push(args);
        };
        win.gtag('js', new Date());
        win.gtag('config', measurementId);
        const script = document.createElement('script');
        script.async = true;
        script.src = `${GA4_SCRIPT_ORIGIN}/gtag/js?id=${encodeURIComponent(measurementId)}`;
        script.onerror = () => {
            win.ga4_load_error = true;
            this.isLoaded = false;
            ctx.logger.warn(`GA4 adapter: failed to load ${measurementId}`);
        };
        script.onload = () => {
            this.isLoaded = true;
        };
        document.head.appendChild(script);
    }
    track(event, integration, _ctx) {
        if (typeof window === 'undefined')
            return;
        const win = window;
        if (win.ga4_load_error === true || typeof win.gtag !== 'function')
            return;
        const mappings = integration.eventMappings ?? [];
        const basePayload = buildBasePayload(event);
        for (const mapping of mappings) {
            if (mapping.internalEvent !== event.kind)
                continue;
            const mapped = applyMapping(basePayload, mapping.parameterMapping);
            win.gtag('event', mapping.externalEvent, mapped);
        }
    }
}
function buildBasePayload(event) {
    const record = {};
    for (const [key, value] of Object.entries(event)) {
        if (key === 'kind')
            continue;
        if (value !== undefined) {
            record[key] = value;
        }
    }
    return record;
}
//# sourceMappingURL=ga4-adapter.js.map